import { useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import usePageMeta from "@/hooks/usePageMeta";
import { categories, countByCategory } from "@/utils/catalog";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/dbb5uoh1375vv1d5bnpg";

const principles = [
  {
    icon: "ri-stack-line",
    title: "只做聚合，不存片源",
    text: "我们把分散的影片基础资料整理成统一结构的条目，站点本身不托管任何视频文件。",
  },
  {
    icon: "ri-route-line",
    title: "按场景分类",
    text: "电影看长片、短片看碎片时间、纪录片看真实世界，不同需求对应不同入口。",
  },
  {
    icon: "ri-shield-check-line",
    title: "信息透明",
    text: "每部影片都标注年份、地区、时长与更新日期，方便你判断是否值得进一步了解。",
  },
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [nickname, setNickname] = useState("");

  usePageMeta(
    "关于我们 - 黄色片免费看",
    "了解黄色片免费看的平台定位、内容范围、联系方式与版权声明，本站仅提供影片资料聚合与浏览服务。",
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setNickname(String(formData.get("nickname") ?? ""));
      form.reset();
      setCharCount(0);
      return;
    }
    formData.delete("contact_alt");

    const message = String(formData.get("message") ?? "").trim();
    if (!message) {
      setStatus("error");
      setFormError("请填写反馈内容后再提交。");
      return;
    }
    if (message.length > 500) {
      setStatus("error");
      setFormError("反馈内容不能超过 500 个字符。");
      return;
    }

    setStatus("idle");
    setFormError("");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") {
        body.append(key, value);
      }
    });

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMessage).toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setNickname(String(formData.get("nickname") ?? ""));
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
        setFormError(serverMessage || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，反馈未提交成功，请稍后重试。");
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">关于我们</span>
          </nav>

          <section className="mt-4 grid grid-cols-1 items-center gap-6 rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-8 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-primary-600">
                <span className="h-1 w-1 rounded-full bg-accent-500" />
                About us
              </span>
              <h1 className="mt-2 font-heading text-2xl font-semibold leading-snug text-foreground-950 md:text-[32px]">
                关于黄色片免费看
              </h1>
              <p className="mt-3 text-sm leading-7 text-foreground-700">
                黄色片免费看是一个面向成年用户的影片资源聚合与浏览平台。我们希望解决一个很具体的问题：
                想看电影却不知道该看什么，想找某一类内容却要在各种页面之间反复跳转。
              </p>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                因此我们把公开可查的影片资料整理成统一条目，按电影、剧集、纪录片、短片与动画分类，
                并提供题材、地区、年代与排序的组合筛选。你可以在几分钟内找到想了解的那一部，而不是刷半小时列表。
              </p>
              <div className="mt-5 flex flex-wrap gap-2.5">
                <Link
                  to="/category/movie"
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  开始浏览影片 <i className="ri-arrow-right-line" />
                </Link>
                <a
                  href="#feedback"
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  <i className="ri-chat-3-line" /> 意见反馈
                </a>
              </div>
            </div>

            <div className="w-full overflow-hidden rounded-lg border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20minimal%20editorial%20photo%20of%20a%20wooden%20desk%20with%20a%20laptop%2C%20stacks%20of%20film%20catalog%20cards%20and%20a%20cup%20of%20coffee%2C%20soft%20morning%20light%20through%20linen%20curtains%2C%20muted%20amber%20and%20cream%20palette%2C%20grainy%20analog%20texture%2C%20calm%20atmosphere%2C%20no%20text&width=900&height=640&seq=about-team-desk&orientation=landscape"
                alt="平台整理影片资料的工作台"
                title="黄色片免费看 - 平台介绍"
                className="h-[240px] w-full object-cover object-top md:h-[300px]"
              />
            </div>
          </section>

          <section className="mt-8">
            <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
              我们坚持的三件事
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4">
              {principles.map((item) => (
                <div
                  key={item.title}
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <h3 className="mt-3 font-heading text-base font-medium text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-8 rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-8">
            <div className="flex flex-col gap-1.5 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                  内容范围
                </h2>
                <p className="mt-1.5 text-sm text-foreground-500">
                  当前已整理的分类与条目数量，点击可直接进入对应列表页。
                </p>
              </div>
              <span className="text-xs text-foreground-400">持续整理中</span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {categories.map((item) => (
                <Link
                  key={item.slug}
                  to={`/category/${item.slug}`}
                  className="group rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <i className={`${item.icon} text-xl text-primary-500`} />
                  <p className="mt-2.5 font-heading text-sm font-medium text-foreground-950 group-hover:text-primary-600">
                    {item.name}
                  </p>
                  <p className="mt-1 text-xs text-foreground-500">
                    {countByCategory(item.slug)} 部资源
                  </p>
                </Link>
              ))}
            </div>
          </section>

          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.1fr]">
            <div className="space-y-5">
              <section id="copyright" className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                  <span className="h-4 w-0.5 rounded-full bg-primary-500" />
                  使用须知与版权声明
                </h2>
                <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-foreground-600">
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-400" />
                    本站仅提供影片资料的聚合、整理与检索服务，不存储、不转码、不提供任何视频文件。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-400" />
                    站内影片名称、简介等资料均为演示示例内容，用于展示页面结构与交互效果。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-400" />
                    页面中的“立即观看”按钮仅为交互演示，不会触发任何真实播放行为。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-400" />
                    本站面向成年用户，未成年人请在监护人指导下离开本页面。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-400" />
                    如认为站内信息存在权利问题，可通过下方反馈入口联系我们处理。
                  </li>
                </ul>
              </section>

              <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                  <span className="h-4 w-0.5 rounded-full bg-secondary-500" />
                  联系方式
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  合作、内容纠正或权利申诉，可通过以下方式联系我们：
                </p>
                <div className="mt-3 space-y-2">
                  <a
                    href="mailto:feedback@example.com"
                    className="flex items-center gap-2.5 rounded-md border border-background-200 bg-background-100/60 px-3.5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
                  >
                    <i className="ri-mail-line text-base text-primary-500" />
                    feedback@example.com
                    <span className="ml-auto text-[11px] text-foreground-400">演示邮箱</span>
                  </a>
                  <div className="flex items-center gap-2.5 rounded-md border border-background-200 bg-background-100/60 px-3.5 py-2.5 text-sm text-foreground-800">
                    <i className="ri-time-line text-base text-primary-500" />
                    处理时效：工作日 48 小时内
                  </div>
                </div>
              </section>
            </div>

            <section
              id="feedback"
              className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
            >
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                <span className="h-4 w-0.5 rounded-full bg-accent-500" />
                意见反馈
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                告诉我们哪里用起来不顺手，或者希望增加哪类影片资源。
              </p>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="mt-6 box-border w-full"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="min-w-0">
                    <label
                      htmlFor="feedback-nickname"
                      className="mb-1.5 block text-xs text-foreground-600"
                    >
                      你的称呼
                    </label>
                    <input
                      id="feedback-nickname"
                      name="nickname"
                      type="text"
                      required
                      placeholder="例如：小周"
                      className="box-border w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>
                  <div className="min-w-0">
                    <label
                      htmlFor="feedback-email"
                      className="mb-1.5 block text-xs text-foreground-600"
                    >
                      联系邮箱
                    </label>
                    <input
                      id="feedback-email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="box-border w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>
                </div>

                <div className="mt-4 min-w-0">
                  <label
                    htmlFor="feedback-type"
                    className="mb-1.5 block text-xs text-foreground-600"
                  >
                    反馈类型
                  </label>
                  <select
                    id="feedback-type"
                    name="type"
                    defaultValue="功能建议"
                    className="box-border w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400"
                  >
                    <option value="功能建议">功能建议</option>
                    <option value="内容纠正">内容纠正</option>
                    <option value="体验问题">体验问题</option>
                    <option value="合作洽谈">合作洽谈</option>
                    <option value="权利申诉">权利申诉</option>
                  </select>
                </div>

                <div className="mt-4 min-w-0">
                  <label
                    htmlFor="feedback-message"
                    className="mb-1.5 flex items-center justify-between text-xs text-foreground-600"
                  >
                    <span>反馈内容</span>
                    <span className={charCount > 500 ? "text-primary-700" : "text-foreground-400"}>
                      {charCount} / 500
                    </span>
                  </label>
                  <textarea
                    id="feedback-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    onChange={(event) => setCharCount(event.target.value.length)}
                    placeholder="请描述你遇到的问题或建议，最多 500 字。"
                    className="box-border w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>

                <div className="form-extra-field" aria-hidden="true">
                  <label htmlFor="feedback-contact-alt">请勿填写</label>
                  <input
                    id="feedback-contact-alt"
                    name="contact_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                {status === "success" && (
                  <div className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
                    <i className="ri-checkbox-circle-line mt-0.5 text-base" />
                    <span>
                      反馈已收到{nickname ? `，谢谢 ${nickname}` : ""}，我们会尽快查看并处理。
                    </span>
                  </div>
                )}
                {status === "error" && (
                  <div className="mt-4 flex items-start gap-2 rounded-md border border-background-300 bg-background-100 px-3.5 py-3 text-sm text-foreground-800">
                    <i className="ri-error-warning-line mt-0.5 text-base text-primary-600" />
                    <span>{formError}</span>
                  </div>
                )}

                <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-[11px] leading-relaxed text-foreground-400">
                    提交即表示同意我们在处理反馈时使用你填写的信息。
                  </p>
                  <button
                    type="submit"
                    className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-send-plane-line" /> 提交反馈
                  </button>
                </div>
              </form>
            </section>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}